import { useState, type ReactNode } from 'react'
import { CheckCircle, Check, Copy, Circle, MinusCircle, WarningCircle, XCircle, WarningOctagon, ArrowClockwise } from '@phosphor-icons/react'

export function Monogram({ className = 'brand-mark' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 476 520" fill="none" stroke="currentColor" strokeWidth="38" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
      <path d="M 104.82 74.50 L 366.46 74.50 A 40.50 40.50 0 0 1 402.59 133.29 L 368.99 199.68 A 63.50 63.50 0 0 1 312.33 234.50 L 158.12 234.50 A 63.50 63.50 0 0 1 101.18 199.11 L 68.50 132.93 A 40.50 40.50 0 0 1 104.82 74.50 Z" />
      <path d="M 158.62 284.50 L 312.06 284.50 A 63.50 63.50 0 0 1 368.75 319.39 L 403.25 387.75 A 40.50 40.50 0 0 1 367.09 446.50 L 104.32 446.50 A 40.50 40.50 0 0 1 68.01 388.07 L 101.68 319.88 A 63.50 63.50 0 0 1 158.62 284.50 Z" />
    </svg>
  )
}

export type Tone = 'pass' | 'review' | 'fail' | 'governed' | 'accent' | 'quiet' | 'neutral'

export function Badge({ tone = 'neutral', children, icon, title }: { tone?: Tone; children: ReactNode; icon?: ReactNode; title?: string }) {
  return (
    <span className={`badge ${tone === 'neutral' ? '' : `is-${tone}`}`} title={title}>
      {icon}
      {children}
    </span>
  )
}

/** A status always carries an icon and a word, never colour alone. */
export function StatusBadge({ status }: { status: 'met' | 'partial' | 'declared' | 'not_reached' | 'failed' | 'reproduced' | 'reversed' | 'not_reproduced' | 'pending' }) {
  const m = {
    met: { tone: 'pass', icon: <CheckCircle size={13} weight="fill" />, text: 'Met' },
    reproduced: { tone: 'pass', icon: <CheckCircle size={13} weight="fill" />, text: 'Reproduced' },
    partial: { tone: 'review', icon: <WarningCircle size={13} weight="fill" />, text: 'Partial' },
    not_reproduced: { tone: 'review', icon: <WarningCircle size={13} weight="fill" />, text: 'Not reproduced' },
    declared: { tone: 'accent', icon: <Circle size={13} weight="bold" />, text: 'Declared' },
    pending: { tone: 'quiet', icon: <Circle size={13} />, text: 'Not run' },
    not_reached: { tone: 'quiet', icon: <MinusCircle size={13} />, text: 'Not reached' },
    failed: { tone: 'fail', icon: <XCircle size={13} weight="fill" />, text: 'Failed' },
    reversed: { tone: 'fail', icon: <XCircle size={13} weight="fill" />, text: 'Reversed' },
  }[status] as { tone: Tone; icon: ReactNode; text: string }
  return (
    <Badge tone={m.tone} icon={m.icon}>
      {m.text}
    </Badge>
  )
}

export function PageHead({ kicker, title, children, actions }: { kicker?: ReactNode; title: ReactNode; children?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head enter">
      <div style={{ minWidth: 0, flex: '1 1 420px' }}>
        {kicker ? <div className="kicker">{kicker}</div> : null}
        <h1>{title}</h1>
        {children ? <p>{children}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </header>
  )
}

export function Card({ title, sub, end, children, className = '', pad = false, id }: { title?: ReactNode; sub?: ReactNode; end?: ReactNode; children: ReactNode; className?: string; pad?: boolean; id?: string }) {
  return (
    <section className={`card ${className}`} id={id} aria-label={typeof title === 'string' ? title : undefined}>
      {title ? (
        <header className="card-head">
          <div style={{ minWidth: 0 }}>
            <h2>{title}</h2>
            {sub ? <div className="sub">{sub}</div> : null}
          </div>
          {end ? <div className="end">{end}</div> : null}
        </header>
      ) : null}
      <div className={pad ? 'card-body' : ''}>{children}</div>
    </section>
  )
}

export function Stat({ label, value, unit, note }: { label: ReactNode; value: ReactNode; unit?: ReactNode; note?: ReactNode }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">
        {value}
        {unit ? <small>{unit}</small> : null}
      </span>
      {note ? <span className="stat-note">{note}</span> : null}
    </div>
  )
}

export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      {children ? <p style={{ maxWidth: '48ch' }}>{children}</p> : null}
      {action}
    </div>
  )
}

export function ErrorState({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <div className="notice is-fail" role="alert">
      <WarningOctagon size={18} weight="fill" />
      <div style={{ flex: 1 }}>{error.message}</div>
      {retry ? (
        <button className="btn btn-sm btn-secondary" onClick={retry}>
          <ArrowClockwise size={14} /> Retry
        </button>
      ) : null}
    </div>
  )
}

export function Skeleton({ h = 18, w = '100%', r }: { h?: number; w?: number | string; r?: number }) {
  return <div className="skeleton" style={{ height: h, width: w, borderRadius: r }} aria-hidden="true" />
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 muted" role="status" style={{ fontSize: 13.5 }}>
      <span className="spinner" aria-hidden="true" /> {label}
    </div>
  )
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      className="btn btn-sm btn-ghost"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true)
          window.setTimeout(() => setDone(false), 1600)
        })
      }}
      aria-label={`${label} to clipboard`}
    >
      {done ? <Check size={14} weight="bold" /> : <Copy size={14} />}
      {done ? 'Copied' : label}
    </button>
  )
}

export function Code({ children, copy = true, label }: { children: string; copy?: boolean; label?: string }) {
  return (
    <div className="card is-sunken" style={{ overflow: 'hidden' }}>
      {label || copy ? (
        <div className="flex items-center gap-2" style={{ padding: '6px 8px 6px 14px', borderBottom: '1px solid var(--border)' }}>
          <span className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
            {label}
          </span>
          <span style={{ marginLeft: 'auto' }}>{copy ? <CopyButton text={children} /> : null}</span>
        </div>
      ) : null}
      <pre style={{ margin: 0, padding: '14px 16px', overflowX: 'auto', fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.6, color: 'var(--text-soft)' }}>
        <code>{children}</code>
      </pre>
    </div>
  )
}

export const Kbd = ({ children }: { children: ReactNode }) => <kbd className="kbd">{children}</kbd>
